import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Badge, Button, EmptyState, FilterBar, Menu, Pagination, Table, TextInput } from '@opendate-app/setlist';
import { DotsVertical, SearchLg } from '@untitled-ui/icons-react';

import {
  ORDER_COLUMNS,
  ORDERS_PER_PAGE,
  DEFAULT_ORDER_SORT,
  ORDER_STATUS_OPTIONS,
  CHECKED_IN_OPTIONS,
  POINT_OF_SALE_OPTIONS,
  buildOrdersQuery,
  serializeOrdersQuery
} from '../constants/orderColumnsConfig';

const TICKET_TYPE_FILTER = 'ticket_type';
const ADD_ON_FILTER = 'add_on';
const RECENT_ORDER_FILTER_KEYS = ['status', 'checked_in', 'point_of_sale', 'payment_method', 'seat_section'];

const decodeHtml = (value) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const decodeRow = (row) => {
  const decoded = Object.fromEntries(Object.entries(row).map(([key, value]) => [key, decodeHtml(value)]));
  return { ...decoded, order: JSON.parse(decoded.json) };
};

const RecentOrdersTable = ({
  team,
  confirm,
  ticketTypes,
  addOns,
  paymentMethods,
  seatSections,
  typedSearchTerm,
  searchedSearchTerm,
  startSearchTermUpdate,
  visibleColumns,
  orderMenuItems,
  onQueryChange
}) => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(DEFAULT_ORDER_SORT);
  const [filterValues, setFilterValues] = useState({});
  const [orders, setOrders] = useState([]);
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const latestRequest = useRef(0);

  const ticketTypeIds = filterValues[TICKET_TYPE_FILTER] || [];
  const addOnIds = filterValues[ADD_ON_FILTER] || [];
  const query = useMemo(() => buildOrdersQuery({ sort, search: searchedSearchTerm }), [sort, searchedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchedSearchTerm]);

  useEffect(() => {
    onQueryChange(query);
  }, [query]);

  useEffect(() => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);

    const params = { ...query, start: (page - 1) * ORDERS_PER_PAGE };
    const dtq = {};
    if (ticketTypeIds.length > 0) dtq.order_items_ticket_type_id_in = ticketTypeIds;
    if (addOnIds.length > 0) dtq.add_on_order_items_add_on_id_in = addOnIds;
    if (Object.keys(dtq).length > 0) params.dtq = dtq;
    const recentOrderFilters = Object.fromEntries(
      RECENT_ORDER_FILTER_KEYS.filter((key) => (filterValues[key] || []).length > 0).map((key) => [key, filterValues[key]])
    );
    if (Object.keys(recentOrderFilters).length > 0) params.filters = recentOrderFilters;

    axios.get(`/teams/${team.id}/confirms/${confirm.id}/dashboard.json`, {
      params,
      paramsSerializer: serializeOrdersQuery
    })
      .then(({ data }) => {
        if (requestId !== latestRequest.current) return;
        setOrders(data.data.map(decodeRow));
        setRecordsFiltered(data.recordsFiltered);
        setError(null);
      })
      .catch(() => {
        if (requestId !== latestRequest.current) return;
        setError('Orders could not be loaded.');
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  }, [team.id, confirm.id, query, page, JSON.stringify(filterValues)]);

  const filters = useMemo(() => [
    {
      key: TICKET_TYPE_FILTER,
      label: 'Ticket Type',
      options: ticketTypes.map((ticketType) => ({ value: String(ticketType.id), label: ticketType.name }))
    },
    ...(addOns.length > 0 ? [{
      key: ADD_ON_FILTER,
      label: 'Add-On',
      options: addOns.map((addOn) => ({ value: String(addOn.id), label: addOn.name }))
    }] : []),
    { key: 'status', label: 'Status', options: ORDER_STATUS_OPTIONS },
    { key: 'checked_in', label: 'Checked In', options: CHECKED_IN_OPTIONS },
    { key: 'point_of_sale', label: 'Point of Sale', options: POINT_OF_SALE_OPTIONS },
    {
      key: 'payment_method',
      label: 'Payment Method',
      options: paymentMethods.map((paymentMethod) => ({ value: paymentMethod, label: paymentMethod }))
    },
    ...(seatSections.length > 0 ? [{
      key: 'seat_section',
      label: 'Section',
      options: seatSections.map((section) => ({ value: section, label: section }))
    }] : [])
  ], [ticketTypes, addOns, paymentMethods, seatSections]);

  const columns = useMemo(() => {
    const cellRenderers = {
      number: (row) => (
        <a href={`/teams/${team.id}/orders/${row.number}`} className="text-primary strong">{row.number}</a>
      ),
      buyer: (row) => (row.buyer_path ? <a href={row.buyer_path} className="text-reset">{row.buyer}</a> : row.buyer),
      date: (row) => <span style={{ color: 'var(--sl-text-tertiary)' }}>{row.date}</span>,
      status: (row) => {
        const status = ORDER_STATUS_OPTIONS.find((option) => option.value === row.status_key);
        return <Badge color={status.color}>{status.label}</Badge>;
      },
      checked_in: (row) => (
        <span style={row.checked_in_state === 'checked_in' ? { color: 'var(--sl-text-success)' } : undefined}>
          {row.checked_in}
        </span>
      )
    };

    return [
      ...visibleColumns.map((column) => {
        const config = ORDER_COLUMNS.find((orderColumn) => orderColumn.id === column.id);
        return {
          key: config.id,
          header: config.label,
          sortable: config.orderable,
          align: config.align,
          primary: config.id === 'number',
          render: cellRenderers[config.id] || ((row) => row[config.id])
        };
      }),
      {
        key: 'menu',
        header: '',
        align: 'right',
        render: (row) => (
          <Menu placement="bottom-end"
                trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Order ${row.number} actions`} />}
                items={orderMenuItems(row.order)} />
        )
      }
    ];
  }, [visibleColumns, team.id, orderMenuItems]);

  const totalPages = Math.max(1, Math.ceil(recordsFiltered / ORDERS_PER_PAGE));
  const firstShown = (page - 1) * ORDERS_PER_PAGE + 1;
  const lastShown = Math.min(page * ORDERS_PER_PAGE, recordsFiltered);
  const orderCountLabel = `${recordsFiltered.toLocaleString()} order${recordsFiltered === 1 ? '' : 's'}`;
  const resultCount = totalPages > 1 ? `${firstShown}–${lastShown} of ${orderCountLabel}` : orderCountLabel;
  const hasFilters = searchedSearchTerm || Object.values(filterValues).some((values) => values.length > 0);

  return (
    <div>
      <div className="mb-3">
        <FilterBar variant="plain"
                   filters={filters}
                   values={filterValues}
                   alwaysShow={filters.map((filter) => filter.key)}
                   onChange={(nextValues) => {
                     setFilterValues(nextValues);
                     setPage(1);
                   }}
                   search={
                     <TextInput size="sm"
                                type="search"
                                leadingIcon={SearchLg}
                                placeholder="Search orders..."
                                aria-label="Search orders"
                                value={typedSearchTerm}
                                onChange={(e) => startSearchTermUpdate(e.target.value)} />
                   }
                   resultCount={resultCount} />
      </div>
      <Table columns={columns}
             data={orders}
             getRowKey={(row) => row.number}
             loading={loading}
             error={error}
             stickyLastColumn
             sort={sort}
             onSortChange={(key, direction) => {
               setSort({ key, direction });
               setPage(1);
             }}
             empty={<EmptyState title={hasFilters ? 'No matching orders' : 'No orders yet'} />} />
      {totalPages > 1 && (
        <Pagination currentPage={page}
                    totalPages={totalPages}
                    onPageChange={(nextPage) => {
                      if (!loading) setPage(nextPage);
                    }} />
      )}
    </div>
  );
};

RecentOrdersTable.propTypes = {
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  addOns: PropTypes.array.isRequired,
  paymentMethods: PropTypes.array.isRequired,
  seatSections: PropTypes.array.isRequired,
  typedSearchTerm: PropTypes.string,
  searchedSearchTerm: PropTypes.string,
  startSearchTermUpdate: PropTypes.func.isRequired,
  visibleColumns: PropTypes.array.isRequired,
  orderMenuItems: PropTypes.func.isRequired,
  onQueryChange: PropTypes.func.isRequired
};

export default RecentOrdersTable;
